
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { listCampingSites } from '@/services/campingSite';
import { CampingSite } from '@/services/campingSite/types';
import { supabase } from "@/integrations/supabase/client";

interface UseCampSitesProps {
  siteType: 'rv' | 'wild-horse';
  locationFilter?: string;
  startDate?: Date;
  endDate?: Date;
}

export const useCampSites = ({ 
  siteType, 
  locationFilter = 'all',
  startDate,
  endDate
}: UseCampSitesProps) => {
  const { toast } = useToast();
  const [sites, setSites] = useState<CampingSite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Function to fetch sites
  const fetchSites = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const dbSiteType = siteType;
      const dbLocation = locationFilter !== 'all' ? locationFilter as any : undefined;
      
      console.log(`Fetching ${dbSiteType} sites for location: ${dbLocation || 'all'}`);
      
      if (startDate && endDate) {
        console.log(`With date filter: ${startDate.toISOString()} to ${endDate.toISOString()}`);
      }
      
      const response = await listCampingSites({
        siteType: dbSiteType,
        location: dbLocation,
        active: true,
        startDate,
        endDate
      });
      
      if (!response || !response.sites) {
        throw new Error('Invalid response from server');
      }
      
      // Add additional logging for availability
      if (startDate && endDate) {
        console.log(`Found ${response.sites.length} sites for the date range`);
        
        // Enhanced logging to better understand site availability
        const availableSites = response.sites.filter(site => site.available).length;
        const unavailableSites = response.sites.filter(site => site.available === false).length;
        const unknownSites = response.sites.filter(site => site.available === undefined).length;
        
        console.log(`Availability breakdown: ${availableSites} available, ${unavailableSites} unavailable, ${unknownSites} unknown`);
      }
      
      setSites(response.sites);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error fetching camping sites:', errorMessage);
      setError('Failed to load camping sites. Please try again later.');
      toast({
        title: "Error",
        description: "Failed to load camping sites. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Initial fetch
  useEffect(() => {
    fetchSites();
  }, [siteType, locationFilter, startDate, endDate, toast]);
  
  // Enhanced real-time subscription - listen for both booking changes and site updates
  useEffect(() => {
    if (sites.length === 0) return;
    
    console.log('Setting up real-time subscriptions for site changes');
    
    // Create channel for site_bookings table changes
    const bookingsChannel = supabase.channel('sites-availability-bookings')
      .on('postgres_changes', 
          { event: '*', schema: 'public', table: 'site_bookings' }, 
          (payload) => {
        console.log('Site booking changed:', payload);
        
        // Safely access the payload properties
        const newData = payload.new as Record<string, any> | null;
        const eventType = payload.eventType;
        
        // Take different actions based on the event type
        if (newData && newData.site_id) {
          switch(eventType) {
            case 'INSERT': // New booking created
              handleBookingCreated(newData);
              break;
            case 'UPDATE': // Booking updated (payment status change, etc)
              handleBookingUpdated(newData);
              break;
            case 'DELETE': // Booking canceled/deleted
              handleBookingDeleted(newData);
              break;
            default:
              console.log(`Unknown event type: ${eventType}`);
          }
        }
      })
      .subscribe((status) => {
        console.log(`Bookings subscription status: ${status}`);
      });
      
    // Create channel for camping_sites table changes  
    const sitesChannel = supabase.channel('sites-availability-sites')
      .on('postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'camping_sites' },
          (payload) => {
        console.log('Camping site updated:', payload);
        
        // If booked_dates was updated, check if it affects our current view
        const newData = payload.new as Record<string, any> | null;
        const oldData = payload.old as Record<string, any> | null;
        const siteId = newData?.id;
        
        if (newData && oldData && siteId) {
          // If status changed (admin flipped active/coming_soon/etc),
          // re-fetch so hidden sites disappear and Coming Soon sites update.
          const statusChanged = newData.status !== oldData.status;
          const statusMessageChanged = newData.status_message !== oldData.status_message;
          if (statusChanged || statusMessageChanged) {
            console.log(`Status changed for site ${siteId}; refetching map sites.`);
            fetchSites();
            return;
          }

          // If booked_dates changed
          if (JSON.stringify(newData.booked_dates) !== JSON.stringify(oldData.booked_dates)) {
            console.log('Booked dates changed for site:', siteId);
            
            // If we have date filters, check if this site is now unavailable
            if (startDate && endDate) {
              // Check if this site is in our current view
              const siteIndex = sites.findIndex(s => s.id === siteId);
              if (siteIndex !== -1) {
                // Check availability
                checkSiteAvailability(siteId, startDate, endDate).then(available => {
                  setSites(prevSites => {
                    const updatedSites = [...prevSites];
                    const site = {...updatedSites[siteIndex]};
                    
                    // Update availability and show notification if it changed to unavailable
                    if (site.available !== !available) {
                      const wasAvailable = site.available !== false;
                      site.available = !available;
                      
                      if (wasAvailable && !site.available) {
                        toast({
                          title: "Site Unavailable",
                          description: `Site ${site.siteNumber} is now unavailable for the selected dates.`,
                          variant: "destructive"
                        });
                      }
                    }
                    
                    updatedSites[siteIndex] = site;
                    return updatedSites;
                  });
                }).catch(() => {
                  // On error, refresh all sites to get fresh data
                  fetchSites();
                });
              }
            } else {
              // No date filters applied, refresh everything
              fetchSites();
            }
          }
        }
      })
      .subscribe((status) => {
        console.log(`Sites subscription status: ${status}`);
      });
    
    console.log('Subscribed to database changes for availability');
    
    // Cleanup subscriptions on unmount
    return () => {
      supabase.removeChannel(bookingsChannel);
      supabase.removeChannel(sitesChannel);
    };
  }, [sites, toast, startDate, endDate]);

  // Helper function to check site availability directly
  const checkSiteAvailability = async (siteId: string, startDate: Date, endDate: Date): Promise<boolean> => {
    try {
      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = endDate.toISOString().split('T')[0];
      
      // Use the database function to check availability
      const { data: isAvailable, error } = await supabase
        .rpc('check_site_availability_for_dates', {
          site_id: siteId,
          check_start_date: formattedStartDate,
          check_end_date: formattedEndDate
        });
      
      if (error) {
        console.error('Error checking site availability:', error);
        return false;
      }
      
      return !!isAvailable;
    } catch (err) {
      console.error('Error in checkSiteAvailability:', err);
      return false;
    }
  };

  // Handler for new booking creation
  const handleBookingCreated = (bookingData: Record<string, any>) => {
    // Only process paid bookings
    if (bookingData.payment_status !== 'paid') {
      return;
    }
    
    setSites(prevSites => {
      // Make a copy of the sites array
      const updatedSites = [...prevSites];
      const affectedSiteIndex = updatedSites.findIndex(s => s.id === bookingData.site_id);
      
      if (affectedSiteIndex !== -1) {
        // Get the affected site
        const updatedSite = {...updatedSites[affectedSiteIndex]};
        const bookingStart = new Date(bookingData.start_date);
        const bookingEnd = new Date(bookingData.end_date);
        
        // Check if booking overlaps with currently selected date range
        const isOverlapping = startDate && endDate && (
          (bookingStart <= endDate && bookingEnd >= startDate)
        );
        
        // If the booking overlaps with our selected dates, mark site as unavailable
        if (isOverlapping) {
          updatedSite.available = false;
          
          toast({
            title: "Site Just Booked",
            description: `Site ${updatedSite.siteNumber} has just been booked for your selected dates.`,
            variant: "destructive"
          });
        }
        
        // Update the booked dates array if it exists
        if (updatedSite.bookedDates) {
          updatedSite.bookedDates = [
            ...updatedSite.bookedDates,
            { start: bookingData.start_date, end: bookingData.end_date }
          ];
        } else {
          updatedSite.bookedDates = [
            { start: bookingData.start_date, end: bookingData.end_date }
          ];
        }
        
        updatedSites[affectedSiteIndex] = updatedSite;
      }
      
      return updatedSites;
    });
  };
  
  // Handler for booking updates
  const handleBookingUpdated = (bookingData: Record<string, any>) => {
    // Only care about status changes to "paid" since that's when a site actually becomes unavailable
    if (bookingData.payment_status === 'paid') {
      setSites(prevSites => {
        // Make a copy of the sites array
        const updatedSites = [...prevSites];
        const affectedSiteIndex = updatedSites.findIndex(s => s.id === bookingData.site_id);
        
        if (affectedSiteIndex !== -1) {
          const updatedSite = {...updatedSites[affectedSiteIndex]};
          const bookingStart = new Date(bookingData.start_date);
          const bookingEnd = new Date(bookingData.end_date);
          
          // Check if booking overlaps with currently selected date range
          const isOverlapping = startDate && endDate && (
            (bookingStart <= endDate && bookingEnd >= startDate)
          );
          
          // If the booking overlaps with our selected dates, mark site as unavailable
          if (isOverlapping) {
            updatedSite.available = false;
            
            toast({
              title: "Booking Confirmed",
              description: `Site ${updatedSite.siteNumber} booking has been confirmed and is no longer available.`,
              variant: "default"
            });
          }
          
          updatedSites[affectedSiteIndex] = updatedSite;
        }
        
        return updatedSites;
      });
    }
  };
  
  // Handler for booking deletions (cancellations)
  const handleBookingDeleted = (bookingData: Record<string, any>) => {
    // For cancellations, it's best to refresh all data
    toast({
      title: "Booking Canceled",
      description: `A booking was just canceled. Refresh to see updated availability.`,
      variant: "default",
      action: (
        <button 
          onClick={fetchSites} 
          className="px-3 py-1 text-xs bg-tribal-primary text-white rounded">
          Refresh
        </button>
      ),
    });
  };

  return {
    sites,
    isLoading,
    error,
    refresh: fetchSites
  };
};
